import React, { useEffect, useRef, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Grid2,
  Box,
  useTheme,
  Theme,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  TextField,
  Chip,
  Avatar,
  InputAdornment,
} from "@mui/material";
import CustomTypography from "../../../components/common/form/CustomTypography";
import CloseIcon from "@mui/icons-material/Close";
import dayjs, { Dayjs } from "dayjs";
import { observer } from "mobx-react";
import { runInAction } from "mobx";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Mail as MailIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import { formatDate } from "../../../components/common/functions/formatDate";
import { useStores } from "../../../../use-stores";
import { useNotification } from "../../../../utils/NotificationsContext";
import { ProjectSourcingProduct } from "../../../../models/ProjectManagement/ProjectSourcingProduct";
import { FilterValue } from "../../../../models/Filters/FilterValue";
import AppSearchDropDown2 from "../../../components/common/filter/AppSearchDropdown2";
import FormSmallTextField from "../../../components/common/form/FormTextField";
import AddEditVendor from "../../vendors/AddEditVendor";
import AddIcon from "@mui/icons-material/Add";
import DatePicker2 from "../../../components/common/textFields/DatePicker2";
import AppDropDown from "../../../components/common/filter/AppDropDown";
import DeleteIcon from "@mui/icons-material/Delete";

import { ProductVariantData } from "../../../../models/PurchaseRequest/PurchaseRequestProductRequestBody";
import AppButton from "../../../components/common/buttons/AppButton";
import CustomTextField from "../../../components/common/textFields/TextField";
import { SortType } from "../../../../models/SortType";

interface CreatePrProps {
  open?: boolean;
  onClose?: () => void;
  orderId: number;
  showSummary?: boolean;
  showGmailRecipients?: boolean;
  enableProductSearch?: boolean;
  handlePr?: () => void;
}

const CreatePurchaseRequestPopup: React.FC<CreatePrProps> = ({
  open = true,
  onClose,
  orderId,
  showSummary = true,
  showGmailRecipients = true,
  enableProductSearch = true,
  handlePr,
}) => {
  const theme = useTheme<Theme>();
  let apiTimer: any;
  const {
    vendorStore,
    projectSourcingOpsStore,
    swageazyInventoryStore,
    addressStore,
    warehouseStore,
    productStore,
  } = useStores();

  const isSwageazyInventory = orderId === -1;
  const { showError } = useNotification();
  const newEmailRef = useRef<HTMLInputElement>(null);
  const productSearchRef = useRef<HTMLDivElement>(null);
  const gmailRegex = RegExp(
    "^[a-zA-Z0-9._%+-]+@(swageazy.com|gmail.com)$",
    "i",
  );
  let productSearchTimer: any;

  const hasSizeVariants = (product: ProjectSourcingProduct): boolean => {
    if (!product?.product_variants || product?.product_variants?.length === 0) {
      return false;
    }
    return (
      product?.product_variants?.some(
        (variant) =>
          variant?.options_name === "Size" || variant?.options_id === 1,
      ) || false
    );
  };

  const resetAllState = () => {
    if (newEmailRef.current) {
      newEmailRef.current.value = "";
    }
    swageazyInventoryStore.productSearchText = "";
    swageazyInventoryStore.managePopup("openvendorPopup", "close");

    if (apiTimer) {
      clearTimeout(apiTimer);
    }
    if (productSearchTimer) {
      clearTimeout(productSearchTimer);
    }

    if (isSwageazyInventory) {
      swageazyInventoryStore.resetCreatePurchaseRequestState();
    } else {
      projectSourcingOpsStore.clearExistingPrProducts();
      runInAction(() => {
        projectSourcingOpsStore.purchaseRequestBody = {
          vendor_id: undefined,
          warehouse_id: undefined,
          expected_delivery_date: undefined,
          is_urgent_sourcing_request: false,
          pr_products: [],
          vendor_payment_term: undefined,
          subtotal: 0,
          total_tax: 0,
          total_amount: 0,
          city_id: undefined,
          city_name: undefined,
          warehouse_name: undefined,
          vendor_name: undefined,
          vendor_email: undefined,
          emails: [],
          other_charges: undefined,
        } as any;
        projectSourcingOpsStore.selectedProductVariants.clear();
      });
    }
  };

  useEffect(() => {
    if (!open) {
      resetAllState();
      return;
    }

    warehouseStore.searchWarehouse().catch((err) => {});

    if (!isSwageazyInventory) {
      projectSourcingOpsStore.purchaseRequestProducts?.forEach((prProduct) => {
        prProduct.quantity = 0;
      });

      projectSourcingOpsStore.calculatePurchaseRequestTotalSum();
    } else {
      // Reset ONLY quantity for Swageazy Inventory, keep cost_price
      swageazyInventoryStore.purchaseRequestProducts?.forEach((prProduct) => {
        prProduct.quantity = 0;
        // Keep cost_price and rate as they are (pre-filled from product data)
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        productSearchRef.current &&
        !productSearchRef.current.contains(event.target as Node)
      ) {
        swageazyInventoryStore.productSearchText = "";
      }
    };

    if (swageazyInventoryStore.productSearchText) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [swageazyInventoryStore.productSearchText]);

  const handleSelectedVendor = (selectedVendor: FilterValue[]) => {
    if (isSwageazyInventory) {
      const vendorId = selectedVendor?.[0]?.id
        ? Number(selectedVendor[0]?.id)
        : undefined;
      const vendorName = selectedVendor?.[0]?.value;

      if (selectedVendor?.length > 0) {
        const vendorPaymentTerm = vendorStore.vendors?.find(
          (vendor) => vendor?.id === Number(selectedVendor[0]?.id),
        );

        const vendorEmail = vendorPaymentTerm?.email?.trim();

        swageazyInventoryStore.setPurchaseRequestFormData({
          ...swageazyInventoryStore.purchaseRequestFormData,
          vendor_id: vendorId,
          vendor_name: vendorName,
          vendor_email: vendorEmail || undefined,
        });
      } else {
        swageazyInventoryStore.setPurchaseRequestFormData({
          ...swageazyInventoryStore.purchaseRequestFormData,
          vendor_id: vendorId,
          vendor_name: vendorName,
          vendor_email: undefined,
        });
      }
    } else {
      projectSourcingOpsStore.updateVendor(
        selectedVendor?.[0]?.id,
        selectedVendor?.[0]?.value,
      );

      if (selectedVendor?.length > 0) {
        const vendorPaymentTerm = vendorStore.vendors?.find(
          (vendor) => vendor?.id === Number(selectedVendor[0]?.id),
        );
        projectSourcingOpsStore.savePrDetails({
          vendor_payment_term: vendorPaymentTerm?.payment_term,
          vendor_email: vendorPaymentTerm?.email?.trim() || undefined,
        });
      } else {
        projectSourcingOpsStore.savePrDetails({
          vendor_payment_term: 0,
          vendor_email: undefined,
        });
      }
    }
  };

  const handleIsUrgentSourcing = () => {
    if (isSwageazyInventory) {
      swageazyInventoryStore.setPurchaseRequestFormData({
        ...swageazyInventoryStore.purchaseRequestFormData,
        is_urgent_sourcing_request:
          !swageazyInventoryStore.purchaseRequestFormData
            ?.is_urgent_sourcing_request,
      });
    } else {
      projectSourcingOpsStore.purchaseRequestBody.is_urgent_sourcing_request =
        !projectSourcingOpsStore.purchaseRequestBody.is_urgent_sourcing_request;
    }
  };

  const handleExpectedDeliveryDate = (expectedDeliveryDate: string) => {
    if (isSwageazyInventory) {
      swageazyInventoryStore.setPurchaseRequestFormData({
        ...swageazyInventoryStore.purchaseRequestFormData,
        expected_delivery_date: expectedDeliveryDate,
      });
    } else {
      projectSourcingOpsStore.savePrDetails({
        expected_delivery_date: expectedDeliveryDate,
      });
    }
  };

  const handleVendorSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        vendorStore
          .getVendors({ page_no: 1, search_text: searchText, status: 4 })
          .catch((err) => {});
      }, 1000);
  };

  // const validateAllVariants = (): {
  //   isValid: boolean;
  //   errorMessage?: string;
  // } => {
  //   const products = isSwageazyInventory
  //     ? swageazyInventoryStore.products
  //     : projectSourcingOpsStore.products;

  //   if (!products || products.length === 0) {
  //     return { isValid: true };
  //   }

  //   const productsWithVariants = products.filter((product) => {
  //     const variants = isSwageazyInventory
  //       ? swageazyInventoryStore.selectedProductVariants.get(product.id) || []
  //       : projectSourcingOpsStore.selectedProductVariants.get(product.id) || [];
  //     return variants.length > 0;
  //   });

  //   if (productsWithVariants.length === 0) {
  //     return { isValid: true };
  //   }

  //   for (const product of productsWithVariants) {
  //     const variants = isSwageazyInventory
  //       ? swageazyInventoryStore.selectedProductVariants.get(product.id) || []
  //       : projectSourcingOpsStore.selectedProductVariants.get(product.id) || [];

  //     if (variants.length === 0) {
  //       continue;
  //     }

  //     const availableOptions =
  //       projectSourcingOpsStore.getAvailableOptions(product);

  //     for (
  //       let variantIndex = 0;
  //       variantIndex < variants.length;
  //       variantIndex++
  //     ) {
  //       const variant = variants[variantIndex];
  //       const variantNumber =
  //         variants.length > 1 ? ` (Variant ${variantIndex + 1})` : "";

  //       if (
  //         !variant.selectedOptions ||
  //         Object.keys(variant.selectedOptions).length === 0
  //       ) {
  //         return {
  //           isValid: false,
  //           errorMessage: `Please select all options for product "${product.name}"${variantNumber}`,
  //         };
  //       }

  //       for (const option of availableOptions) {
  //         const selectedValue = variant.selectedOptions?.[option.optionId];
  //         if (!selectedValue || selectedValue.trim() === "") {
  //           return {
  //             isValid: false,
  //             errorMessage: `Please select ${option.optionName} for product "${product.name}"${variantNumber}`,
  //           };
  //         }
  //       }
  //       if (
  //         variant.quantity === null ||
  //         variant.quantity === undefined ||
  //         variant.quantity <= 0 ||
  //         isNaN(Number(variant.quantity))
  //       ) {
  //         return {
  //           isValid: false,
  //           errorMessage: `Please enter a valid quantity (greater than 0) for product "${product.name}"${variantNumber}`,
  //         };
  //       }

  //       if (
  //         variant.costPrice === null ||
  //         variant.costPrice === undefined ||
  //         variant.costPrice <= 0 ||
  //         isNaN(Number(variant.costPrice))
  //       ) {
  //         return {
  //           isValid: false,
  //           errorMessage: `Please enter a valid cost price (greater than 0) for product "${product.name}"${variantNumber}`,
  //         };
  //       }
  //     }
  //   }

  //   return { isValid: true };
  // };

  const createPr = () => {
    // const validation = validateAllVariants();
    // if (!validation.isValid) {
    //   showError(
    //     "Validation Error",
    //     validation.errorMessage ||
    //       "Please fill all required fields for all variants"
    //   );
    //   return;
    // }

    if (handlePr) {
      handlePr();
    }
  };

  // const handleCitySearch = (searchText?: string) => {
  //   if (apiTimer) clearTimeout(apiTimer);
  //   if (searchText && searchText.length > 2)
  //     apiTimer = setTimeout(() => {
  //       addressStore
  //         .getCity({
  //           page_no: 1,
  //           search_text: searchText,
  //         })
  //         .catch((err) => { });
  //     }, 1000);
  // };

  const handleWarehouseSearch = () => {
    if (isSwageazyInventory) {
      const cityId = swageazyInventoryStore.purchaseRequestFormData?.city_id;
      if (cityId) {
        warehouseStore
          .searchWarehouse(Number(cityId))
          .then(() => {
            if (warehouseStore.warehouses?.length === 1) {
              swageazyInventoryStore.setPurchaseRequestFormData({
                ...swageazyInventoryStore.purchaseRequestFormData,
                warehouse_id: warehouseStore.warehouses?.[0]?.id,
                warehouse_name: warehouseStore.warehouses?.[0]?.city_sku,
              });
            }
          })
          .catch((err) => {});
      }
    } else {
      if (projectSourcingOpsStore.purchaseRequestBody?.city_id)
        warehouseStore
          .searchWarehouse(
            Number(projectSourcingOpsStore.purchaseRequestBody?.city_id),
          )
          .then(() => {
            if (warehouseStore.warehouses?.length === 1) {
              projectSourcingOpsStore.updateWarehouse(
                warehouseStore.warehouses?.[0]?.id,
                warehouseStore.warehouses?.[0]?.city_sku,
              );
            }
          })
          .catch((err) => {});
    }
  };

  const handleAddEmail = () => {
    const emailValue = newEmailRef.current?.value || "";
    const trimmedEmail = emailValue.trim();
    if (trimmedEmail && gmailRegex.test(trimmedEmail)) {
      const currentEmails = isSwageazyInventory
        ? swageazyInventoryStore.purchaseRequestFormData?.emails || []
        : projectSourcingOpsStore.purchaseRequestBody?.emails || [];

      if (
        !currentEmails.some(
          (email) => email.toLowerCase() === trimmedEmail.toLowerCase(),
        )
      ) {
        const updatedEmails = [...currentEmails, trimmedEmail];
        if (newEmailRef.current) {
          newEmailRef.current.value = "";
        }
        if (isSwageazyInventory) {
          swageazyInventoryStore.setPurchaseRequestFormData({
            ...swageazyInventoryStore.purchaseRequestFormData,
            emails: updatedEmails,
          });
        } else {
          runInAction(() => {
            projectSourcingOpsStore.purchaseRequestBody.emails = updatedEmails;
          });
        }
      } else {
        showError("Error occurred", "This email is already added");
      }
    } else {
      showError("Error occurred", "Please enter a valid Gmail address");
    }
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    const currentEmails = isSwageazyInventory
      ? swageazyInventoryStore.purchaseRequestFormData?.emails || []
      : projectSourcingOpsStore.purchaseRequestBody?.emails || [];

    const updatedEmails = currentEmails.filter(
      (email) => email.toLowerCase() !== emailToRemove.toLowerCase(),
    );

    if (isSwageazyInventory) {
      swageazyInventoryStore.setPurchaseRequestFormData({
        ...swageazyInventoryStore.purchaseRequestFormData,
        emails: updatedEmails,
      });
    } else {
      runInAction(() => {
        projectSourcingOpsStore.purchaseRequestBody.emails = updatedEmails;
      });
    }
  };

  const handleProductSearch = (searchText?: string) => {
    swageazyInventoryStore.setProductSearchText(searchText || "");
    if (productSearchTimer) clearTimeout(productSearchTimer);
    if (searchText && searchText.length > 2) {
      productSearchTimer = setTimeout(async () => {
        try {
          await productStore.getProducts({
            page_no: 1,
            limit: 50,
            sortBy: "name",
            sortType: SortType.ASC,
            name: searchText,
          });
        } catch (err) {
          // Error handling
        }
      }, 500);
    }
  };

  const filteredProducts = useMemo(() => {
    if (
      !swageazyInventoryStore.productSearchText ||
      swageazyInventoryStore.productSearchText.length <= 2
    ) {
      return [];
    }
    return (
      productStore.products?.map((product) => ({
        id: product?.id || 0,
        name: product?.name || "",
        hsn_code: product?.hsn_code || "",
        gst_percentage: product?.gst_percentage || 0,
        cost_price: product?.cost_price || 0,
        image_url: product?.image_urls?.[0]?.url || product?.image_url || "",
      })) || []
    );
  }, [swageazyInventoryStore.productSearchText, productStore.products]);

  const downloadPO = (po_id: number) => {
    projectSourcingOpsStore
      .downloadPurchaseOrder(po_id)
      .then((response) => {
        if (response.data) {
          const downloadUrl = window.URL.createObjectURL(response.data);
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.download = response.filename
            ? response.filename
            : `PO_${po_id}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          return showError("Error Occurred", "Failed to download PO.");
        }
      })
      .catch((err) => {
        showError(
          "Error Occurred",
          err?.message || "Failed to download PO, please try again later",
        );
      });
  };

  const handleClose = () => {
    resetAllState();
    onClose?.();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          transition: {
            timeout: 300,
            sx: {
              backgroundColor: theme.palette.customColors.backdrop,
              backdropFilter: "blur(4px)",
            },
          },
          backdrop: {
            timeout: 300,
            sx: {
              backgroundColor: theme.palette.customColors.backdrop,
              backdropFilter: "blur(4px)",
            },
          },
          paper: {
            sx: {
              borderRadius: "16px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
              height: "90vh",

              maxWidth: {
                xs: "90vw",
                sm: "90vw",
                md: "80vw",
                lg: "80vw",
                xl: "70vw",
              },
              width: {
                xs: "90vw",
                sm: "90vw",
                md: "80vw",
                lg: "80vw",
                xl: "70vw",
              },
              margin: { xs: "16px", sm: "32px" },
              background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
            },
          },
        }}
        sx={{
          zIndex: 2000,
          "& .MuiDialog-container": {
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            position: "relative",
            height: "100%",
            minHeight: "85vh",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
              borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
            }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Box>
                <CustomTypography
                  variant="body2"
                  style={{
                    color: theme.palette.customColors.greyTextColor,
                    marginBottom: 4,
                  }}
                >
                  {orderId !== -1
                    ? `Order ID: #${orderId}`
                    : "Swageazy Inventory"}
                </CustomTypography>
                <CustomTypography variant="heading3">
                  Create Purchase Request
                </CustomTypography>
              </Box>
            </Box>

            <IconButton
              onClick={handleClose}
              sx={{
                color: theme.palette.customColors.greyTextColor,
                "&:hover": {
                  backgroundColor: theme.palette.customColors.lightGreyColor,
                  color: theme.palette.customColors.blackBorder,
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <DialogContent
            sx={{
              padding: { xs: "20px", sm: "24px" },
              flex: 1,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-track": {
                backgroundColor: theme.palette.customColors.greyBackground,
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.palette.customColors.greyDarkborder,
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: theme.palette.customColors.greyTextColor,
                },
              },
            }}
          >
            <Box
              sx={{
                mb: 2,
                display: "flex",
                gap: 4,
                flexDirection: { xs: "column", sm: "row" },
                flexWrap: { xs: "nowrap", sm: "wrap" },
              }}
            >
              <Box
                sx={{
                  flex: { xs: "none", sm: "1 1 auto" },
                  mb: 2,
                  minWidth: { sm: 0 },
                }}
              >
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 6 }}
                >
                  Select Vendor{" "}
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  style={{
                    marginBottom: 20,
                    lineHeight: "1.2",
                    color: theme.palette.text.secondary,
                  }}
                >
                  Search the vendor from vendors list.
                </CustomTypography>
                <Box
                  display="flex"
                  alignItems="center"
                  gap="0.75rem"
                  sx={{
                    width: "100%",
                    backgroundColor: theme.palette.customColors.white,
                    border: `1px solid ${theme.palette.customColors.greyBorder}`,
                    borderRadius: "8px",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ width: "100%" }}>
                      <AppSearchDropDown2
                        isMultiSelect={false}
                        title={"Select Vendor"}
                        local={false}
                        backgroundColor={
                          theme.palette.customColors.grayBackGroundColor
                        }
                        borderColor={
                          theme.palette.customColors.greyBodyBackground
                        }
                        handleSearch={handleVendorSearch}
                        list={
                          vendorStore.vendors?.map((item) => {
                            return { id: String(item?.id), value: item?.name! };
                          }) || []
                        }
                        handleSelect={(selectedVendor: FilterValue[]) => {
                          handleSelectedVendor(selectedVendor);
                        }}
                        value={
                          isSwageazyInventory
                            ? swageazyInventoryStore.purchaseRequestFormData
                                ?.vendor_id
                              ? [
                                  {
                                    id: String(
                                      swageazyInventoryStore
                                        .purchaseRequestFormData?.vendor_id,
                                    ),
                                    value:
                                      swageazyInventoryStore
                                        .purchaseRequestFormData?.vendor_name ??
                                      "",
                                  },
                                ]
                              : []
                            : projectSourcingOpsStore.purchaseRequestBody
                                  ?.vendor_id
                              ? [
                                  {
                                    id: String(
                                      projectSourcingOpsStore
                                        .purchaseRequestBody?.vendor_id,
                                    ),
                                    value:
                                      projectSourcingOpsStore
                                        .purchaseRequestBody?.vendor_name ?? "",
                                  },
                                ]
                              : []
                        }
                      />
                    </Box>
                  </Box>

                  <IconButton
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                      marginRight: "0.85rem",
                      color: theme.palette.customColors.darkBlue,
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                    size="small"
                    onClick={() =>
                      swageazyInventoryStore.managePopup(
                        "openvendorPopup",
                        "open",
                      )
                    }
                  >
                    <AddIcon fontSize="small" />
                    <CustomTypography
                      variant="heading8"
                      color={theme.palette.customColors.darkBlue}
                    >
                      New
                    </CustomTypography>
                  </IconButton>
                </Box>

                {(isSwageazyInventory
                  ? swageazyInventoryStore.purchaseRequestFormData?.vendor_id
                  : projectSourcingOpsStore.purchaseRequestBody?.vendor_id) &&
                  Number(
                    isSwageazyInventory
                      ? swageazyInventoryStore.purchaseRequestFormData
                          ?.vendor_id
                      : projectSourcingOpsStore.purchaseRequestBody?.vendor_id,
                  ) === Number(process.env.REACT_APP_VENDOR_ID) && (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap="0.85rem"
                      mt={2.5}
                    >
                      {/* <AppSearchDropDown2
                        isMultiSelect={false}
                        title={"Select City"}
                        local={false}
                        backgroundColor={
                          theme.palette.customColors.grayBackGroundColor
                        }
                        borderColor={
                          theme.palette.customColors.greyBodyBackground
                        }
                        minWidth="13.938rem"
                        handleSearch={handleCitySearch}
                        list={addressStore.cities?.map((item) => {
                          return { id: String(item.id), value: item.name! };
                        })}
                        handleSelect={(selectedCity: FilterValue[]) => {
                          if (isSwageazyInventory) {
                            if (
                              selectedCity?.length > 0 &&
                              selectedCity[0]?.id
                            ) {
                              swageazyInventoryStore.setPurchaseRequestFormData(
                                {
                                  ...swageazyInventoryStore.purchaseRequestFormData,
                                  city_id: String(selectedCity[0]?.id),
                                  city_name: selectedCity?.[0]?.value,
                                }
                              );
                              handleWarehouseSearch();
                            } else {
                              swageazyInventoryStore.setPurchaseRequestFormData(
                                {
                                  ...swageazyInventoryStore.purchaseRequestFormData,
                                  city_id: undefined,
                                  city_name: undefined,
                                  warehouse_id: undefined,
                                  warehouse_name: undefined,
                                }
                              );
                            }
                          } else {
                            if (
                              selectedCity?.length > 0 &&
                              selectedCity[0]?.id
                            ) {
                              projectSourcingOpsStore.updateCity(
                                selectedCity?.[0]?.id,
                                selectedCity?.[0]?.value
                              );
                              projectSourcingOpsStore.updateWarehouse();
                              handleWarehouseSearch();
                            } else {
                              projectSourcingOpsStore.updateCity(
                                undefined,
                                undefined
                              );
                              projectSourcingOpsStore.updateWarehouse(
                                undefined,
                                undefined
                              );
                            }
                          }
                        }}
                        value={
                          isSwageazyInventory
                            ? swageazyInventoryStore.purchaseRequestFormData
                              ?.city_id
                              ? [
                                {
                                  id: String(
                                    swageazyInventoryStore
                                      .purchaseRequestFormData?.city_id
                                  ),
                                  value:
                                    swageazyInventoryStore
                                      .purchaseRequestFormData?.city_name ??
                                    "",
                                },
                              ]
                              : []
                            : projectSourcingOpsStore.purchaseRequestBody
                              ?.city_id
                              ? [
                                {
                                  id: projectSourcingOpsStore
                                    .purchaseRequestBody?.city_id,
                                  value:
                                    projectSourcingOpsStore.purchaseRequestBody
                                      ?.city_name ?? "",
                                },
                              ]
                              : []
                        }
                      /> */}

                      <AppSearchDropDown2
                        isMultiSelect={false}
                        title={"Select Warehouse"}
                        local={true}
                        backgroundColor={
                          theme.palette.customColors.grayBackGroundColor
                        }
                        borderColor={
                          theme.palette.customColors.greyBodyBackground
                        }
                        minWidth="13.938rem"
                        list={
                          warehouseStore.warehouses?.map((item) => {
                            return {
                              id: String(item?.id),
                              value: String(item?.id),
                            };
                          }) || []
                        }
                        handleSelect={(selectedItem: FilterValue[]) => {
                          if (isSwageazyInventory) {
                            swageazyInventoryStore.setPurchaseRequestFormData({
                              ...swageazyInventoryStore.purchaseRequestFormData,
                              warehouse_id: selectedItem?.[0]?.id || undefined,
                              warehouse_name:
                                selectedItem?.[0]?.id || undefined,
                            });
                          } else {
                            projectSourcingOpsStore.updateWarehouse(
                              selectedItem?.[0]?.id,
                              selectedItem?.[0]?.id,
                            );
                          }
                        }}
                        value={
                          isSwageazyInventory
                            ? swageazyInventoryStore.purchaseRequestFormData
                                ?.warehouse_id
                              ? [
                                  {
                                    id: String(
                                      swageazyInventoryStore
                                        .purchaseRequestFormData?.warehouse_id,
                                    ),
                                    value: String(
                                      swageazyInventoryStore
                                        .purchaseRequestFormData?.warehouse_id,
                                    ),
                                  },
                                ]
                              : []
                            : projectSourcingOpsStore.purchaseRequestBody
                                  ?.warehouse_id
                              ? [
                                  {
                                    id: projectSourcingOpsStore
                                      .purchaseRequestBody?.warehouse_id,
                                    value:
                                      projectSourcingOpsStore
                                        .purchaseRequestBody?.warehouse_id ??
                                      "",
                                  },
                                ]
                              : []
                        }
                      />
                    </Box>
                  )}
              </Box>

              <Box
                sx={{
                  flex: { xs: "none", sm: "1 1 250px" },
                  mb: 2,
                  minWidth: { sm: "250px" },
                }}
              >
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 6 }}
                >
                  Select Delivery Date{" "}
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  style={{
                    marginBottom: 20,
                    lineHeight: "1.2",
                    color: theme.palette.text.secondary,
                  }}
                >
                  The date can't be select as past date
                </CustomTypography>
                <DatePicker2
                  label=""
                  type="date"
                  minDate={dayjs()}
                  value={null}
                  onChange={(date: Dayjs | null) => {
                    if (!date) return;

                    const dateWithTime = date.startOf("day");
                    const formattedDate = dateWithTime.format(
                      "YYYY-MM-DD HH:mm:ss",
                    );
                    handleExpectedDeliveryDate(formattedDate);
                  }}
                  zIndex={3000}
                />
              </Box>
            </Box>

            {showGmailRecipients && (
              <Box
                sx={{
                  mb: 3,
                  borderRadius: "12px",
                  padding: { xs: "12px", sm: "16px" },
                  backgroundColor: theme.palette.common.white,
                  border: `1px solid ${theme.palette.customColors.greyBorder}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: { sm: "flex-start" },
                    justifyContent: { sm: "flex-start", lg: "space-between" },
                    flexDirection: { xs: "column", lg: "row" },
                    flexWrap: "wrap",
                    gap: { xs: 2, sm: 2, md: 4 },
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      width: { xs: "100%", lg: "calc(50% - 16px)" },
                      flexShrink: 0,
                      boxSizing: "border-box",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        background:
                          theme.palette.customColors.blueGradientStart ||
                          "#6455ED",
                        boxShadow: "0 4px 12px rgba(100, 85, 237, 0.25)",
                      }}
                    >
                      <MailIcon
                        sx={{
                          color: theme.palette.customColors.white,
                          fontSize: 20,
                        }}
                      />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: { sm: "100%", md: 0 } }}>
                      <CustomTypography
                        variant="body1"
                        style={{ fontWeight: 600, marginBottom: 2 }}
                      >
                        Recipient Email
                      </CustomTypography>
                      <CustomTypography
                        variant="caption"
                        style={{
                          color: theme.palette.text.secondary,
                          lineHeight: 1.3,
                        }}
                      >
                        Add Email accounts that will receive this purchase
                        request email.
                      </CustomTypography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      width: { xs: "100%", lg: "calc(50% - 16px)" },
                      flexShrink: 0,
                      boxSizing: "border-box",
                    }}
                  >
                    <TextField
                      inputRef={newEmailRef}
                      fullWidth
                      size="small"
                      placeholder="Add email address"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddEmail();
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleAddEmail}
                              edge="end"
                              size="small"
                              sx={{
                                color:
                                  theme.palette.customColors
                                    .blueGradientStart ||
                                  theme.palette.primary.main,
                                padding: "4px",
                                "&:hover": {
                                  backgroundColor:
                                    theme.palette.customColors
                                      .grayBackGroundColor,
                                },
                              }}
                            >
                              <AddCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                          height: "36px",
                          "& fieldset": {
                            borderColor:
                              theme.palette.customColors.greyDarkborder,
                          },
                          "&:hover fieldset": {
                            borderColor:
                              theme.palette.customColors.blueGradientStart,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor:
                              theme.palette.customColors.blueGradientStart,
                          },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "0.875rem",
                          padding: "8px 14px",
                          "&::placeholder": {
                            fontSize: "0.75rem",
                            opacity: 0.7,
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>

                {(() => {
                  const emails = isSwageazyInventory
                    ? swageazyInventoryStore.purchaseRequestFormData?.emails ||
                      []
                    : projectSourcingOpsStore.purchaseRequestBody?.emails || [];
                  return emails && emails.length > 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        borderRadius: "10px",
                        padding: "8px 10px",
                        backgroundColor:
                          theme.palette.customColors.grayBackGroundColor ??
                          "#F7F7FB",
                        minHeight: 40,
                      }}
                    >
                      {emails.map((email) => (
                        <Chip
                          key={email}
                          size="small"
                          label={email}
                          onDelete={() => handleRemoveEmail(email)}
                          avatar={
                            <Avatar
                              sx={{
                                width: 22,
                                height: 22,
                                backgroundColor:
                                  theme.palette.customColors
                                    .blueGradientStart ||
                                  theme.palette.primary.main,
                              }}
                            >
                              <MailIcon
                                sx={{
                                  color: theme.palette.common.white,
                                  fontSize: 16,
                                }}
                              />
                            </Avatar>
                          }
                          sx={{
                            "& .MuiChip-deleteIcon": {
                              color: theme.palette.error.main,
                              "&:hover": {
                                color: theme.palette.error.dark,
                              },
                            },
                          }}
                        />
                      ))}
                    </Box>
                  ) : null;
                })()}
              </Box>
            )}

            {enableProductSearch && (
              <Box sx={{ mb: 3 }}>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 8 }}
                >
                  Add Products{" "}
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                </CustomTypography>
                <Box sx={{ position: "relative" }} ref={productSearchRef}>
                  <CustomTextField
                    placeholder="Search products..."
                    value={swageazyInventoryStore.productSearchText}
                    onChange={(e) => handleProductSearch(e.target.value)}
                    width={"100%"}
                  />
                  {filteredProducts.length > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        mt: 1,
                        backgroundColor: theme.palette.customColors.white,
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        maxHeight: "300px",
                        overflow: "auto",
                      }}
                    >
                      {filteredProducts.map((product) => (
                        <Box
                          key={product?.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            padding: "12px 16px",
                            cursor: "pointer",
                            justifyContent: "space-between",
                            "&:hover": {
                              backgroundColor:
                                theme.palette.customColors.lightGreyColor,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <img
                              src={product?.image_url || ""}
                              alt={product?.name}
                              style={{
                                width: "40px",
                                height: "40px",
                                objectFit: "contain",
                                borderRadius: "4px",
                                backgroundColor:
                                  theme.palette.customColors.greyBackground,
                              }}
                            />
                            <CustomTypography variant="body2" sx={{ flex: 1 }}>
                              {product?.name}
                            </CustomTypography>
                          </Box>
                          <Box>
                            {(() => {
                              const isProductAdded = isSwageazyInventory
                                ? swageazyInventoryStore.purchaseRequestProducts?.some(
                                    (p) => p?.id === product?.id,
                                  )
                                : projectSourcingOpsStore.purchaseRequestProducts?.some(
                                    (p) => p?.id === product?.id,
                                  );

                              return (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const fullProduct =
                                      productStore.products?.find(
                                        (p) => p?.id === product?.id,
                                      );

                                    const productVariants =
                                      (fullProduct as any)?.product_variants ||
                                      fullProduct?.variants ||
                                      [];

                                    if (isSwageazyInventory) {
                                      const existingProduct =
                                        swageazyInventoryStore.purchaseRequestProducts?.find(
                                          (p) => p?.id === product?.id,
                                        );
                                      if (!existingProduct) {
                                        const productCostPrice =
                                          fullProduct?.cost_price ||
                                          (product as any)?.cost_price ||
                                          0;
                                        const productGstPercentage =
                                          fullProduct?.gst_percentage ||
                                          (product as any)?.gst_percentage ||
                                          undefined;
                                        swageazyInventoryStore.purchaseRequestProducts =
                                          [
                                            ...(swageazyInventoryStore.purchaseRequestProducts ||
                                              []),
                                            {
                                              id: product?.id,
                                              description: "",
                                              quantity: 0,
                                              cost_price: productCostPrice,
                                              rate: productCostPrice,
                                              gst_percentage:
                                                productGstPercentage,
                                            },
                                          ];
                                        const existingInProducts =
                                          swageazyInventoryStore.products?.find(
                                            (p) => p?.id === product?.id,
                                          );
                                        if (!existingInProducts) {
                                          const newProduct = {
                                            id: product?.id,
                                            name: product?.name,
                                            description: "",
                                            quantity: 0,
                                            used_quantity: 0,
                                            cost_price: productCostPrice,
                                            unit_price: 0,
                                            category_id:
                                              fullProduct?.category_id || 0,
                                            category_name:
                                              fullProduct?.category_name || "",
                                            hsn_code: product?.hsn_code || "",
                                            gst_percentage:
                                              product?.gst_percentage || 0,
                                            sku: "",
                                            variants: [],
                                            product_variants: productVariants,
                                          } as ProjectSourcingProduct;
                                          swageazyInventoryStore.products = [
                                            ...(swageazyInventoryStore.products ||
                                              []),
                                            newProduct,
                                          ];

                                          // if (hasSizeVariants(newProduct)) {
                                          //   const existingVariants =
                                          //     swageazyInventoryStore.selectedProductVariants.get(
                                          //       product?.id
                                          //     ) || [];
                                          //   const productInStore =
                                          //     swageazyInventoryStore.products?.find(
                                          //       (p) => p?.id === product?.id
                                          //     );
                                          //   const variantCostPrice =
                                          //     productInStore?.cost_price ||
                                          //     product?.cost_price ||
                                          //     null;

                                          //   const availableOptions =
                                          //     projectSourcingOpsStore.getAvailableOptions(
                                          //       newProduct
                                          //     );
                                          //   const autoSelectedOptions: {
                                          //     [key: string]: string;
                                          //   } = {};

                                          //   availableOptions.forEach(
                                          //     (option) => {
                                          //       if (
                                          //         option.values &&
                                          //         option.values.length === 1
                                          //       ) {
                                          //         autoSelectedOptions[
                                          //           option.optionId.toString()
                                          //         ] = option.values[0].value;
                                          //       }
                                          //     }
                                          //   );

                                          //   const newVariant: ProductVariantData =
                                          //     {
                                          //       id: `${
                                          //         product?.id
                                          //       }-${Date.now()}`,
                                          //       productId: product?.id,
                                          //       selectedOptions:
                                          //         autoSelectedOptions,
                                          //       quantity: null,
                                          //       costPrice: variantCostPrice,
                                          //     };

                                          //   const matchingVariant =
                                          //     projectSourcingOpsStore.findMatchingVariant(
                                          //       newProduct,
                                          //       autoSelectedOptions
                                          //     );
                                          //   if (
                                          //     matchingVariant &&
                                          //     matchingVariant?.cost_price
                                          //   ) {
                                          //     newVariant.costPrice =
                                          //       matchingVariant.cost_price;
                                          //   }

                                          //   swageazyInventoryStore.selectedProductVariants.set(
                                          //     product?.id,
                                          //     [...existingVariants, newVariant]
                                          //   );
                                          // }
                                        }
                                      } else {
                                        swageazyInventoryStore.purchaseRequestProducts =
                                          swageazyInventoryStore.purchaseRequestProducts?.filter(
                                            (p) => p?.id !== product?.id,
                                          ) || [];
                                        swageazyInventoryStore.selectedProductVariants.delete(
                                          product?.id,
                                        );
                                        swageazyInventoryStore.products =
                                          swageazyInventoryStore.products?.filter(
                                            (p) => p?.id !== product?.id,
                                          ) || [];
                                      }
                                    } else {
                                      const existingProduct =
                                        projectSourcingOpsStore.purchaseRequestProducts?.find(
                                          (p) => p?.id === product?.id,
                                        );
                                      if (!existingProduct) {
                                        const newProduct = {
                                          id: product?.id,
                                          name: product?.name,
                                          description: "",
                                          quantity: 0,
                                          used_quantity: 0,
                                          cost_price: 0,
                                          unit_price: 0,
                                          category_id:
                                            fullProduct?.category_id || 0,
                                          category_name:
                                            fullProduct?.category_name || "",
                                          hsn_code: product?.hsn_code || "",
                                          gst_percentage:
                                            product?.gst_percentage || 0,
                                          sku: "",
                                          variants: [],
                                          product_variants: productVariants,
                                        } as ProjectSourcingProduct;
                                        projectSourcingOpsStore.handleSingleSelectProductForPr(
                                          newProduct,
                                        );

                                        const existingInProducts =
                                          projectSourcingOpsStore.products?.find(
                                            (p) => p?.id === product?.id,
                                          );
                                        if (!existingInProducts) {
                                          projectSourcingOpsStore.products = [
                                            ...(projectSourcingOpsStore.products ||
                                              []),
                                            newProduct,
                                          ];
                                        }

                                        // if (hasSizeVariants(newProduct)) {
                                        //   projectSourcingOpsStore.addProductVariant(
                                        //     product?.id
                                        //   );

                                        //   const variants =
                                        //     projectSourcingOpsStore.selectedProductVariants.get(
                                        //       product?.id
                                        //     ) || [];
                                        //   const lastVariant =
                                        //     variants[variants.length - 1];

                                        //   if (lastVariant) {
                                        //     const availableOptions =
                                        //       projectSourcingOpsStore.getAvailableOptions(
                                        //         newProduct
                                        //       );
                                        //     const autoSelectedOptions: {
                                        //       [key: string]: string;
                                        //     } = {};

                                        //     availableOptions.forEach(
                                        //       (option) => {
                                        //         if (
                                        //           option.values &&
                                        //           option.values.length === 1
                                        //         ) {
                                        //           autoSelectedOptions[
                                        //             option.optionId.toString()
                                        //           ] = option.values[0].value;
                                        //         }
                                        //       }
                                        //     );

                                        //     if (
                                        //       Object.keys(autoSelectedOptions)
                                        //         .length > 0
                                        //     ) {
                                        //       projectSourcingOpsStore.updateProductVariant(
                                        //         product?.id,
                                        //         lastVariant.id,
                                        //         "selectedOptions",
                                        //         autoSelectedOptions
                                        //       );

                                        //       const matchingVariant =
                                        //         projectSourcingOpsStore.findMatchingVariant(
                                        //           newProduct,
                                        //           autoSelectedOptions
                                        //         );
                                        //       if (
                                        //         matchingVariant &&
                                        //         matchingVariant?.cost_price
                                        //       ) {
                                        //         projectSourcingOpsStore.updateProductVariant(
                                        //           product?.id,
                                        //           lastVariant.id,
                                        //           "costPrice",
                                        //           matchingVariant.cost_price
                                        //         );
                                        //       }
                                        //     }
                                        //   }
                                        // }
                                      } else {
                                        projectSourcingOpsStore.handleSingleSelectProductForPr(
                                          {
                                            id: product?.id,
                                            name: product?.name,
                                            description: "",
                                            quantity: 0,
                                            used_quantity: 0,
                                            cost_price: 0,
                                            unit_price: 0,
                                            category_id:
                                              fullProduct?.category_id || 0,
                                            category_name:
                                              fullProduct?.category_name || "",
                                            hsn_code: product?.hsn_code || "",
                                            gst_percentage:
                                              product?.gst_percentage || 0,
                                            sku: "",
                                            variants: [],
                                            product_variants: productVariants,
                                          } as ProjectSourcingProduct,
                                        );
                                      }
                                    }
                                  }}
                                  sx={{
                                    width: "32px",
                                    height: "32px",
                                    backgroundColor: isProductAdded
                                      ? theme.palette.error.main
                                      : theme.palette.customColors
                                          .blueGradientStart || "#6455ED",
                                    color: theme.palette.customColors.white,
                                    borderRadius: "50%",
                                    "&:hover": {
                                      backgroundColor: isProductAdded
                                        ? theme.palette.error.dark
                                        : theme.palette.customColors
                                            .blueGradientEnd || "#7B6FED",
                                      transform: "scale(1.1)",
                                    },
                                  }}
                                >
                                  {isProductAdded ? (
                                    <RemoveIcon fontSize="small" />
                                  ) : (
                                    <AddIcon fontSize="small" />
                                  )}
                                </IconButton>
                              );
                            })()}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Grid2
              container
              spacing={2}
              alignItems="start"
              justifyContent="space-between"
            >
              <Grid2
                size={{
                  xs: 12,
                  sm: 12,
                  md: 12,
                  lg: showSummary ? 8 : 12,
                  xl: showSummary ? 8 : 12,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    borderRadius: "12px",
                    backgroundColor: "rgba(248, 249, 250, 0.3)",
                    border: "1px solid rgba(0,0,0,0.05)",
                    padding: "16px",
                  }}
                >
                  {(isSwageazyInventory
                    ? (swageazyInventoryStore.purchaseRequestProducts ?? [])
                    : (projectSourcingOpsStore.purchaseRequestProducts ?? [])
                  ).length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "200px",
                        textAlign: "center",
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        fontWeight={700}
                        color="rgba(218, 218, 218, 1)"
                      >
                        NO PRODUCTS SELECTED!
                      </CustomTypography>
                    </Box>
                  ) : (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {(isSwageazyInventory
                        ? swageazyInventoryStore.products
                        : projectSourcingOpsStore.products
                      )
                        ?.filter((product) =>
                          (isSwageazyInventory
                            ? swageazyInventoryStore.purchaseRequestProducts
                            : projectSourcingOpsStore.purchaseRequestProducts
                          )?.some((pr) => pr?.id === product?.id),
                        )
                        ?.map((product: ProjectSourcingProduct, index) => (
                          <Box key={product?.id}>
                            {/* Product Card */}
                            <Box
                              sx={{
                                position: "relative",
                                backgroundColor: theme.palette.background.paper,
                                borderRadius: "12px",
                                border: `1px solid ${theme.palette.customColors.greyBorder}`,
                                padding: "20px 20px 0px 20px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                                  borderColor:
                                    theme.palette.customColors.pinkDark + "40",
                                },
                              }}
                            >
                              {orderId == -1 && (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (isSwageazyInventory) {
                                      swageazyInventoryStore.purchaseRequestProducts =
                                        swageazyInventoryStore.purchaseRequestProducts?.filter(
                                          (p) => p?.id !== product?.id,
                                        ) || [];
                                      swageazyInventoryStore.selectedProductVariants.delete(
                                        product?.id,
                                      );
                                    } else {
                                      projectSourcingOpsStore.handleSingleSelectProductForPr(
                                        product,
                                      );
                                    }
                                  }}
                                  sx={{
                                    position: "absolute",
                                    top: "8px",
                                    right: "8px",
                                    color: theme.palette.error.main,
                                    padding: "4px",
                                    borderRadius: "50%",
                                    width: "24px",
                                    height: "24px",
                                    zIndex: 10,
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 3,
                                  alignItems: "flex-start",
                                  flexWrap: { xs: "wrap", sm: "nowrap" },
                                }}
                              >
                                {/* Product Image and Info */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 2,
                                    flex: 1,
                                    minWidth: { xs: "100%", sm: "300px" },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      background: "rgba(242, 242, 242, 1)",
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      minHeight: "3.5rem",
                                      maxHeight: "3.5rem",
                                      minWidth: "3.5rem",
                                      maxWidth: "3.5rem",
                                      borderRadius: "0.375rem",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <img
                                      style={{
                                        maxHeight: "2.5rem",
                                        minHeight: "2.5rem",
                                        maxWidth: "2.5rem",
                                        minWidth: "2.5rem",
                                        objectFit: "contain",
                                      }}
                                      src={
                                        product?.mockups?.[0]?.image_url
                                          ? product?.mockups?.[0]?.image_url
                                          : (product?.image_url ??
                                            "https://procureasystorage.s3.ap-south-1.amazonaws.com/logos/SELogoSmall.png")
                                      }
                                      alt={product?.name}
                                    />
                                  </Box>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",

                                        gap: 3,
                                        mb: 1,
                                      }}
                                    >
                                      <CustomTypography
                                        variant="body3"
                                        fontWeight={700}
                                        sx={{
                                          color: theme.palette.text.secondary,
                                          backgroundColor:
                                            theme.palette.customColors
                                              .grayBackGroundColor,
                                          padding: "4px 8px",
                                          borderRadius: "4px",
                                        }}
                                      >
                                        ID: {product?.id}
                                      </CustomTypography>

                                      <CustomTypography
                                        variant="body2"
                                        fontWeight={600}
                                        title={product?.name}
                                        sx={{ cursor: "default" }}
                                      >
                                        {product?.name}
                                      </CustomTypography>
                                    </Box>
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap="0.3rem"
                                      fontSize="0.75rem"
                                      color={
                                        theme.palette.customColors.greyTextColor
                                      }
                                      fontWeight="600"
                                      flexWrap="wrap"
                                      mb={1}
                                    >
                                      {orderId !== -1 && (
                                        <Box
                                          display="flex"
                                          alignItems="center"
                                          sx={{
                                            borderRight:
                                              "2px solid rgba(209, 209, 209, 1)",
                                            paddingRight: "0.5rem",
                                            gap: "2px",
                                          }}
                                        >
                                          Qty:
                                          <CustomTypography
                                            variant="body3"
                                            fontWeight={600}
                                            title={product?.quantity?.toString()}
                                            sx={{ cursor: "default" }}
                                          >
                                            {product?.quantity || 0}
                                          </CustomTypography>
                                        </Box>
                                      )}
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        sx={{
                                          borderRight:
                                            "1px solid rgba(209, 209, 209, 1)",
                                          paddingRight: "0.3rem",
                                          gap: "1px",
                                        }}
                                      >
                                        HSN:
                                        <CustomTypography
                                          variant="body3"
                                          fontWeight={600}
                                          sx={{ cursor: "default" }}
                                        >
                                          {product?.hsn_code || "--"}
                                        </CustomTypography>
                                      </Box>
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        sx={{
                                          borderRight:
                                            "1px solid rgba(209, 209, 209, 1)",
                                          paddingRight: "0.3rem",
                                          gap: "1px",
                                        }}
                                      >
                                        GST:
                                        <CustomTypography
                                          variant="body3"
                                          fontWeight={600}
                                          sx={{ cursor: "default" }}
                                        >
                                          {product?.gst_percentage || "--"}
                                        </CustomTypography>
                                      </Box>
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        gap="1px"
                                      >
                                        Color:
                                        <CustomTypography
                                          variant="body3"
                                          fontWeight={600}
                                          sx={{ cursor: "default" }}
                                        >
                                          {"--"}
                                        </CustomTypography>
                                      </Box>
                                    </Box>

                                    <Box sx={{ mb: 1 }}>
                                      <FormSmallTextField
                                        id={String(product?.id)}
                                        value={
                                          isSwageazyInventory
                                            ? swageazyInventoryStore.purchaseRequestProducts?.find(
                                                (pr) => pr?.id === product?.id,
                                              )?.description ||
                                              product?.description ||
                                              ""
                                            : projectSourcingOpsStore.purchaseRequestProducts?.find(
                                                (pr) => pr?.id === product?.id,
                                              )?.description ||
                                              product?.description ||
                                              ""
                                        }
                                        onChange={(id, value) => {
                                          if (isSwageazyInventory) {
                                            const updatedProducts =
                                              swageazyInventoryStore.purchaseRequestProducts?.map(
                                                (p) =>
                                                  p?.id === Number(id)
                                                    ? {
                                                        ...p,
                                                        description:
                                                          value || "",
                                                      }
                                                    : p,
                                              ) || [];
                                            swageazyInventoryStore.purchaseRequestProducts =
                                              updatedProducts;
                                          } else {
                                            projectSourcingOpsStore.updatePrProductDescription(
                                              Number(id),
                                              value || "",
                                            );
                                          }
                                        }}
                                        sx={{
                                          marginBottom: 0,
                                          "& .MuiOutlinedInput-input": {
                                            paddingLeft: 0,
                                            paddingRight: 0,
                                            paddingBottom: 0,
                                            paddingTop: "4px",
                                            fontSize: "12px",
                                          },
                                          "& input::placeholder": {
                                            fontSize: "12px",
                                          },
                                          "& .MuiOutlinedInput-notchedOutline":
                                            {
                                              borderTop: "none",
                                              borderLeft: "none",
                                              borderRight: "none",
                                            },
                                          "& .MuiOutlinedInput-root": {
                                            borderRadius: 0,
                                            minHeight: "28px",
                                          },
                                        }}
                                        placeholder="Add description..."
                                      />
                                    </Box>

                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 2,
                                        mt: 2,
                                        mb: 2,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      <Box sx={{ flex: "1 1 150px" }}>
                                        <FormSmallTextField
                                          type="number"
                                          title="Quantity"
                                          value={(() => {
                                            const prProduct =
                                              isSwageazyInventory
                                                ? swageazyInventoryStore.purchaseRequestProducts?.find(
                                                    (pr) =>
                                                      pr?.id === product?.id,
                                                  )
                                                : projectSourcingOpsStore.purchaseRequestProducts?.find(
                                                    (pr) =>
                                                      pr?.id === product?.id,
                                                  );

                                            return prProduct?.quantity &&
                                              prProduct.quantity > 0
                                              ? prProduct.quantity
                                              : "";
                                          })()}
                                          onChange={(id, value) => {
                                            const quantity =
                                              value && value.trim() !== ""
                                                ? Number(value)
                                                : undefined;
                                            if (isSwageazyInventory) {
                                              const updatedProducts =
                                                swageazyInventoryStore.purchaseRequestProducts?.map(
                                                  (p) =>
                                                    p?.id === product?.id
                                                      ? {
                                                          ...p,
                                                          quantity: quantity,
                                                        }
                                                      : p,
                                                ) || [];
                                              swageazyInventoryStore.purchaseRequestProducts =
                                                updatedProducts;

                                              const subtotal =
                                                updatedProducts.reduce(
                                                  (sum, p) => {
                                                    return (
                                                      sum +
                                                      (p.quantity || 0) *
                                                        (p.cost_price || 0)
                                                    );
                                                  },
                                                  0,
                                                );
                                              const otherCharges =
                                                swageazyInventoryStore
                                                  .purchaseRequestFormData
                                                  ?.other_charges || 0;
                                              const totalAmount =
                                                subtotal + otherCharges;

                                              swageazyInventoryStore.setPurchaseRequestFormData(
                                                {
                                                  ...swageazyInventoryStore.purchaseRequestFormData,
                                                  subtotal: subtotal,
                                                  total_amount: totalAmount,
                                                },
                                              );
                                            } else {
                                              const updatedProducts =
                                                projectSourcingOpsStore.purchaseRequestProducts?.map(
                                                  (p) =>
                                                    p?.id === product?.id
                                                      ? {
                                                          ...p,
                                                          quantity: quantity,
                                                        }
                                                      : p,
                                                ) || [];
                                              projectSourcingOpsStore.purchaseRequestProducts =
                                                updatedProducts;
                                              projectSourcingOpsStore.calculatePurchaseRequestTotalSum();
                                            }
                                          }}
                                          sx={{ marginBottom: "0px" }}
                                          placeholder="Enter quantity"
                                        />
                                      </Box>
                                      <Box sx={{ flex: "1 1 150px" }}>
                                        <FormSmallTextField
                                          type="number"
                                          title="Cost Price"
                                          value={(() => {
                                            const prProduct =
                                              isSwageazyInventory
                                                ? swageazyInventoryStore.purchaseRequestProducts?.find(
                                                    (pr) =>
                                                      pr?.id === product?.id,
                                                  )
                                                : projectSourcingOpsStore.purchaseRequestProducts?.find(
                                                    (pr) =>
                                                      pr?.id === product?.id,
                                                  );

                                            return prProduct?.cost_price !==
                                              undefined &&
                                              prProduct?.cost_price !== null
                                              ? prProduct.cost_price
                                              : "";
                                          })()}
                                          onChange={(id, value) => {
                                            const costPrice =
                                              value && value.trim() !== ""
                                                ? Number(value)
                                                : undefined;
                                            if (isSwageazyInventory) {
                                              const updatedProducts =
                                                swageazyInventoryStore.purchaseRequestProducts?.map(
                                                  (p) =>
                                                    p?.id === product?.id
                                                      ? {
                                                          ...p,
                                                          cost_price: costPrice,
                                                          rate: costPrice,
                                                        }
                                                      : p,
                                                ) || [];
                                              swageazyInventoryStore.purchaseRequestProducts =
                                                updatedProducts;

                                              const subtotal =
                                                updatedProducts.reduce(
                                                  (sum, p) => {
                                                    return (
                                                      sum +
                                                      (p.quantity || 0) *
                                                        (p.cost_price || 0)
                                                    );
                                                  },
                                                  0,
                                                );
                                              const otherCharges =
                                                swageazyInventoryStore
                                                  .purchaseRequestFormData
                                                  ?.other_charges || 0;
                                              const totalAmount =
                                                subtotal + otherCharges;

                                              swageazyInventoryStore.setPurchaseRequestFormData(
                                                {
                                                  ...swageazyInventoryStore.purchaseRequestFormData,
                                                  subtotal: subtotal,
                                                  total_amount: totalAmount,
                                                },
                                              );
                                            } else {
                                              const updatedProducts =
                                                projectSourcingOpsStore.purchaseRequestProducts?.map(
                                                  (p) =>
                                                    p?.id === product?.id
                                                      ? {
                                                          ...p,
                                                          cost_price: costPrice,
                                                          rate: costPrice,
                                                        }
                                                      : p,
                                                ) || [];
                                              projectSourcingOpsStore.purchaseRequestProducts =
                                                updatedProducts;
                                              projectSourcingOpsStore.calculatePurchaseRequestTotalSum();
                                            }
                                          }}
                                          sx={{ marginBottom: "0px" }}
                                          placeholder="Enter cost price"
                                        />
                                      </Box>
                                    </Box>
                                  </Box>
                                </Box>

                                {/* <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    alignItems: "flex-end",
                                    minWidth: { xs: "100%", sm: "auto" },
                                    marginTop: "16px",
                                  }}
                                >
                                  <AppButton
                                    label="Add Variant"
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                      if (!hasSizeVariants(product)) {
                                        showError(
                                          "No Size Available",
                                          "No size available for this product. Go to product and add variant."
                                        );
                                        return;
                                      }

                                      if (isSwageazyInventory) {
                                        const existingVariants =
                                          swageazyInventoryStore.selectedProductVariants.get(
                                            product?.id
                                          ) || [];
                                        const productInStore =
                                          swageazyInventoryStore.products?.find(
                                            (p) => p?.id === product?.id
                                          );
                                        const productCostPrice =
                                          productInStore?.cost_price ||
                                          product?.cost_price ||
                                          null;
                                        const newVariant: ProductVariantData = {
                                          id: `${product?.id}-${Date.now()}`,
                                          productId: product?.id,
                                          selectedOptions: {},
                                          quantity: null,
                                          costPrice: productCostPrice,
                                        };
                                        swageazyInventoryStore.selectedProductVariants.set(
                                          product?.id,
                                          [...existingVariants, newVariant]
                                        );
                                      } else {
                                        projectSourcingOpsStore.addProductVariant(
                                          product?.id
                                        );
                                      }
                                    }}
                                    variant="transparentPrimary"
                                    customStyles={{
                                      width: "100%",
                                      height: "36px !important",
                                      minHeight: "36px !important",
                                      maxHeight: "36px !important",
                                      "&:hover": {
                                        transform: "translateY(-1px)",
                                      },
                                    }}
                                  />
                                </Box> */}
                              </Box>

                              {/* {(
                                (isSwageazyInventory
                                  ? swageazyInventoryStore.selectedProductVariants.get(
                                      product.id
                                    )
                                  : projectSourcingOpsStore.selectedProductVariants.get(
                                      product.id
                                    )) || []
                              ).length > 0 && ( */}
                              {false && (
                                <Box
                                  sx={{
                                    mt: 2,
                                    pb: 2,
                                  }}
                                >
                                  {!hasSizeVariants(product) ? (
                                    <Box
                                      sx={{
                                        backgroundColor:
                                          theme.palette.customColors
                                            .grayBackGroundColor,
                                        border: `1px solid ${theme.palette.customColors.greyBorder}`,
                                        borderRadius: "8px",
                                        padding: "16px",
                                        textAlign: "center",
                                      }}
                                    >
                                      <CustomTypography
                                        variant="body2"
                                        style={{
                                          color: theme.palette.error.main,
                                          fontWeight: 500,
                                        }}
                                      >
                                        No size available for this product. Go
                                        to product and add variant.
                                      </CustomTypography>
                                    </Box>
                                  ) : (
                                    <>
                                      <CustomTypography
                                        variant="body3"
                                        fontWeight={600}
                                        style={{
                                          color: theme.palette.text.secondary,
                                          marginBottom: 10,
                                          textTransform: "uppercase",
                                          letterSpacing: "0.5px",
                                          fontSize: "0.7rem",
                                        }}
                                      >
                                        Variant Options
                                      </CustomTypography>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 2,
                                        }}
                                      >
                                        {(
                                          (isSwageazyInventory
                                            ? swageazyInventoryStore.selectedProductVariants.get(
                                                product.id,
                                              )
                                            : projectSourcingOpsStore.selectedProductVariants.get(
                                                product.id,
                                              )) || []
                                        ).map(
                                          (product_variant, variantIndex) => (
                                            <Box
                                              key={`${product?.id}-variant-${product_variant?.id}`}
                                              sx={{
                                                backgroundColor:
                                                  theme.palette.customColors
                                                    .grayBackGroundColor,
                                                border: `1px solid ${theme.palette.customColors.greyBorder}`,
                                                borderRadius: "8px",
                                                padding: "16px",
                                                transition: "all 0.2s ease",
                                                "&:hover": {
                                                  borderColor:
                                                    theme.palette.customColors
                                                      .pinkDark + "40",
                                                },
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  gap: 1.5,
                                                  alignItems: "flex-start",
                                                  flexWrap: "wrap",
                                                  width: "100%",
                                                  maxWidth: "100%",
                                                }}
                                              >
                                                {/* Option Dropdowns */}
                                                {(isSwageazyInventory
                                                  ? projectSourcingOpsStore.getAvailableOptions(
                                                      product,
                                                    )
                                                  : projectSourcingOpsStore.getAvailableOptions(
                                                      product,
                                                    )
                                                ).map((option) => (
                                                  <Box
                                                    key={option.optionId}
                                                    sx={{
                                                      flex: "1 1 0",
                                                      minWidth: {
                                                        xs: "100%",
                                                        sm: "140px",
                                                      },
                                                      maxWidth: {
                                                        xs: "100%",
                                                        sm: "180px",
                                                      },
                                                    }}
                                                  >
                                                    <AppDropDown
                                                      value={
                                                        product_variant
                                                          ?.selectedOptions?.[
                                                          option?.optionId
                                                        ] || ""
                                                      }
                                                      list={option.values}
                                                      fontSize="0.8rem"
                                                      isMultiSelect={false}
                                                      title={`Select ${option.optionName}`}
                                                      menuZIndex={3000}
                                                      onSingleSelectionChange={(
                                                        selectedValue,
                                                      ) => {
                                                        const newSelectedOptions =
                                                          {
                                                            ...product_variant?.selectedOptions,
                                                            [option?.optionId]:
                                                              selectedValue,
                                                          };

                                                        if (
                                                          isSwageazyInventory
                                                        ) {
                                                          const existingVariants =
                                                            swageazyInventoryStore.selectedProductVariants.get(
                                                              product?.id,
                                                            ) || [];
                                                          const updatedVariants =
                                                            existingVariants?.map(
                                                              (v) =>
                                                                v?.id ===
                                                                product_variant?.id
                                                                  ? {
                                                                      ...v,
                                                                      selectedOptions:
                                                                        newSelectedOptions,
                                                                    }
                                                                  : v,
                                                            ) || [];
                                                          swageazyInventoryStore.selectedProductVariants.set(
                                                            product?.id,
                                                            updatedVariants,
                                                          );

                                                          const matchingVariant =
                                                            projectSourcingOpsStore.findMatchingVariant(
                                                              product,
                                                              newSelectedOptions,
                                                            );
                                                          if (
                                                            matchingVariant &&
                                                            matchingVariant?.cost_price
                                                          ) {
                                                            const updatedVariantsWithPrice =
                                                              updatedVariants?.map(
                                                                (v) =>
                                                                  v?.id ===
                                                                  product_variant?.id
                                                                    ? {
                                                                        ...v,
                                                                        costPrice:
                                                                          matchingVariant?.cost_price ??
                                                                          null,
                                                                      }
                                                                    : v,
                                                              ) || [];
                                                            swageazyInventoryStore.selectedProductVariants.set(
                                                              product?.id,
                                                              updatedVariantsWithPrice,
                                                            );
                                                          }
                                                        } else {
                                                          projectSourcingOpsStore.updateProductVariant(
                                                            product?.id,
                                                            product_variant?.id,
                                                            "selectedOptions",
                                                            newSelectedOptions,
                                                          );

                                                          const matchingVariant =
                                                            projectSourcingOpsStore.findMatchingVariant(
                                                              product,
                                                              newSelectedOptions,
                                                            );
                                                          if (
                                                            matchingVariant &&
                                                            matchingVariant?.cost_price
                                                          ) {
                                                            projectSourcingOpsStore.updateProductVariant(
                                                              product?.id,
                                                              product_variant?.id,
                                                              "costPrice",
                                                              matchingVariant?.cost_price,
                                                            );
                                                          }
                                                        }
                                                      }}
                                                      borderColor={
                                                        theme.palette
                                                          .customColors
                                                          .greyDarkborder
                                                      }
                                                      borderRadius="8px"
                                                    />
                                                  </Box>
                                                ))}

                                                <Box
                                                  sx={{
                                                    flex: "1 1 0",
                                                    minWidth: {
                                                      xs: "100%",
                                                      sm: "140px",
                                                    },
                                                    maxWidth: {
                                                      xs: "100%",
                                                      sm: "180px",
                                                    },
                                                  }}
                                                >
                                                  <FormSmallTextField
                                                    type="number"
                                                    title="Quantity"
                                                    value={
                                                      product_variant?.quantity ||
                                                      ""
                                                    }
                                                    onChange={(id, value) => {
                                                      if (isSwageazyInventory) {
                                                        const existingVariants =
                                                          swageazyInventoryStore.selectedProductVariants.get(
                                                            product?.id,
                                                          ) || [];
                                                        const updatedVariants =
                                                          existingVariants?.map(
                                                            (v) =>
                                                              v?.id ===
                                                              product_variant?.id
                                                                ? {
                                                                    ...v,
                                                                    quantity:
                                                                      Number(
                                                                        value,
                                                                      ),
                                                                  }
                                                                : v,
                                                          ) || [];
                                                        swageazyInventoryStore.selectedProductVariants.set(
                                                          product?.id,
                                                          updatedVariants,
                                                        );
                                                      } else {
                                                        projectSourcingOpsStore.updateProductVariant(
                                                          product?.id,
                                                          product_variant?.id,
                                                          "quantity",
                                                          Number(value),
                                                        );
                                                      }
                                                    }}
                                                    sx={{
                                                      marginBottom: "0px",
                                                      width: "100%",
                                                    }}
                                                  />
                                                </Box>

                                                {/* Cost Price Input */}
                                                <Box
                                                  sx={{
                                                    flex: "1 1 0",
                                                    minWidth: {
                                                      xs: "100%",
                                                      sm: "140px",
                                                    },
                                                    maxWidth: {
                                                      xs: "100%",
                                                      sm: "180px",
                                                    },
                                                  }}
                                                >
                                                  <FormSmallTextField
                                                    type="number"
                                                    title="Cost Price"
                                                    value={
                                                      product_variant?.costPrice ||
                                                      ""
                                                    }
                                                    onChange={(id, value) => {
                                                      if (isSwageazyInventory) {
                                                        const existingVariants =
                                                          swageazyInventoryStore.selectedProductVariants.get(
                                                            product?.id,
                                                          ) || [];
                                                        const updatedVariants =
                                                          existingVariants?.map(
                                                            (v) =>
                                                              v?.id ===
                                                              product_variant?.id
                                                                ? {
                                                                    ...v,
                                                                    costPrice:
                                                                      Number(
                                                                        value,
                                                                      ),
                                                                  }
                                                                : v,
                                                          ) || [];
                                                        swageazyInventoryStore.selectedProductVariants.set(
                                                          product?.id,
                                                          updatedVariants,
                                                        );
                                                      } else {
                                                        projectSourcingOpsStore.updateProductVariant(
                                                          product?.id,
                                                          product_variant?.id,
                                                          "costPrice",
                                                          Number(value),
                                                        );
                                                      }
                                                    }}
                                                    sx={{
                                                      marginBottom: "0px",
                                                      width: "100%",
                                                    }}
                                                    placeholder="Enter price"
                                                  />
                                                </Box>

                                                {/* Delete Button */}
                                                <Box
                                                  sx={{
                                                    flex: "0 0 auto",
                                                    minWidth: {
                                                      xs: "100%",
                                                      sm: "auto",
                                                    },
                                                    maxWidth: {
                                                      xs: "100%",
                                                      sm: "60px",
                                                    },
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: {
                                                      xs: "flex-start",
                                                      sm: "center",
                                                    },
                                                  }}
                                                >
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                      if (isSwageazyInventory) {
                                                        const existingVariants =
                                                          swageazyInventoryStore.selectedProductVariants.get(
                                                            product?.id,
                                                          ) || [];
                                                        const filteredVariants =
                                                          existingVariants?.filter(
                                                            (v) =>
                                                              v?.id !==
                                                              product_variant?.id,
                                                          ) || [];
                                                        swageazyInventoryStore.selectedProductVariants.set(
                                                          product?.id,
                                                          filteredVariants,
                                                        );
                                                      } else {
                                                        projectSourcingOpsStore.removeProductVariant(
                                                          product?.id,
                                                          product_variant?.id,
                                                        );
                                                      }
                                                    }}
                                                    sx={{
                                                      color:
                                                        theme.palette.error
                                                          .main,
                                                      padding: "6px",
                                                      backgroundColor:
                                                        theme.palette.error
                                                          .light + "10",
                                                      borderRadius: "6px",
                                                      "&:hover": {
                                                        backgroundColor:
                                                          theme.palette.error
                                                            .light + "25",
                                                        transform:
                                                          "scale(1.05)",
                                                      },
                                                      transition:
                                                        "all 0.2s ease",
                                                    }}
                                                  >
                                                    <DeleteIcon fontSize="small" />
                                                  </IconButton>
                                                </Box>
                                              </Box>
                                            </Box>
                                          ),
                                        )}
                                      </Box>
                                    </>
                                  )}
                                </Box>
                              )}

                              {/* PO History Accordion - Inside Product Card, No spacing */}
                              {(() => {
                                const insights =
                                  projectSourcingOpsStore
                                    .purchaseRequestProductInsights[product.id];
                                return insights && insights.length > 0;
                              })() && (
                                <Box
                                  sx={{
                                    mt: 0,
                                    pt: 1,
                                  }}
                                >
                                  <Accordion
                                    sx={{
                                      boxShadow: "none",
                                      paddingBottom: "0.5rem",
                                      "&:before": { display: "none" },
                                    }}
                                    disableGutters
                                  >
                                    <AccordionSummary
                                      aria-controls={`panel-${product.id}-content`}
                                      id={`panel-${product.id}-header`}
                                      sx={{
                                        minHeight: "1rem",
                                        maxHeight: "1rem",
                                        backgroundColor: "transparent",
                                        padding: "0.5rem",
                                        paddingTop: 0,
                                        "& .MuiAccordionSummary-content": {
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "flex-end",
                                          width: "100%",
                                          margin: 0,
                                        },
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "end",
                                        }}
                                      >
                                        <CustomTypography
                                          variant="body3"
                                          fontWeight={700}
                                          color={
                                            theme.palette.customColors.pinkDark
                                          }
                                        >
                                          PO History
                                        </CustomTypography>
                                        <ExpandMoreIcon
                                          sx={{
                                            color:
                                              theme.palette.customColors
                                                .pinkDark,
                                          }}
                                        />
                                      </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ paddingBottom: 0 }}>
                                      {projectSourcingOpsStore.purchaseRequestProductInsights?.[
                                        product?.id
                                      ]?.map((insight, index) => (
                                        <Box
                                          paddingBottom={
                                            projectSourcingOpsStore
                                              ?.purchaseRequestProductInsights?.[
                                              product?.id
                                            ] &&
                                            projectSourcingOpsStore
                                              ?.purchaseRequestProductInsights?.[
                                              product?.id
                                            ]?.length > 0
                                              ? "1rem"
                                              : "0"
                                          }
                                          key={index}
                                        >
                                          <Box
                                            sx={{
                                              background:
                                                index % 2 === 0
                                                  ? theme.palette.customColors
                                                      .grayBackGroundColor
                                                  : theme.palette.customColors
                                                      .white,
                                              borderRadius: "0.25rem",
                                              padding: "0.5rem",
                                            }}
                                          >
                                            <CustomTypography variant="body3">
                                              {insight?.title} - PR{" "}
                                              <a
                                                href={`/purchase/requests/details?prId=${insight?.pr_id}&projectId=${orderId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                  textDecoration: "underline",
                                                  color: "inherit",
                                                }}
                                              >
                                                {insight?.pr_id}
                                              </a>{" "}
                                              was raised for{" "}
                                              {insight?.customer_name} @{" "}
                                              {insight?.rate} by{" "}
                                              {insight?.created_by_admin_name}{" "}
                                              on this{" "}
                                              {formatDate(
                                                insight?.created_date,
                                              )}
                                              {". Download PO "}
                                              <span
                                                onClick={() =>
                                                  downloadPO(insight?.po_id)
                                                }
                                                style={{
                                                  textDecoration: "underline",
                                                  color: "blue",
                                                  cursor: "pointer",
                                                }}
                                              >
                                                {insight?.po_id}
                                              </span>{" "}
                                              {"."}
                                            </CustomTypography>
                                          </Box>
                                        </Box>
                                      ))}
                                    </AccordionDetails>
                                  </Accordion>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        ))}
                    </Box>
                  )}
                </Box>
              </Grid2>

              {showSummary && (
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}>
                  <Box
                    sx={{
                      width: "100%",
                      padding: "1.5rem",
                      borderRadius: "12px",
                      boxShadow: "0px 4px 20px 0px rgba(215, 223, 231, 0.5)",
                      backgroundColor: theme.palette.customColors.white,
                      border: `1px solid ${theme.palette.customColors.greyBorder}`,
                    }}
                  >
                    <CustomTypography variant="heading4" fontWeight={600}>
                      Summary
                    </CustomTypography>

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      paddingTop="1.5rem"
                      flexWrap="wrap"
                    >
                      <CustomTypography
                        variant="body2"
                        color={theme.palette.customColors.tableGrayHeadingColor}
                      >
                        Payment Term
                      </CustomTypography>
                      <CustomTypography
                        variant="body2"
                        color={theme.palette.customColors.tableGrayHeadingColor}
                      >
                        {isSwageazyInventory
                          ? swageazyInventoryStore.purchaseRequestFormData
                              ?.vendor_payment_term || 0
                          : projectSourcingOpsStore.purchaseRequestBody
                              .vendor_payment_term || 0}{" "}
                        Days
                      </CustomTypography>
                    </Box>
                    <Divider sx={{ mt: "0.625rem" }} />

                    <Box
                      paddingTop="0.5rem"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <CustomTypography
                        variant="body2"
                        color={theme.palette.customColors.tableGrayHeadingColor}
                      >
                        Subtotal
                      </CustomTypography>
                      <CustomTypography
                        variant="body2"
                        color={theme.palette.customColors.tableGrayHeadingColor}
                      >
                        ₹
                        {isSwageazyInventory
                          ? (
                              swageazyInventoryStore.purchaseRequestFormData
                                ?.subtotal || 0
                            ).toFixed(2)
                          : (
                              projectSourcingOpsStore.purchaseRequestBody
                                .subtotal || 0
                            ).toFixed(2)}
                      </CustomTypography>
                    </Box>
                    <Divider sx={{ mt: "0.625rem" }} />

                    <Box
                      paddingTop="0.5rem"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <Box>
                        <CustomTypography
                          variant="body2"
                          color={
                            theme.palette.customColors.tableGrayHeadingColor
                          }
                        >
                          Other Charges<span style={{ color: "red" }}>*</span>
                        </CustomTypography>
                      </Box>
                      <Box>
                        <FormSmallTextField
                          variant="standard"
                          color="secondary"
                          type="number"
                          fontSize="0.875rem"
                          sx={{
                            width: "100%",
                            maxWidth: "80px",
                            paddingTop: "0px !important",
                            marginTop: "0px !important",
                            paddingBottom: "0px !important",
                            paddingLeft: "0px !important",
                            marginBottom: "0px !important",
                          }}
                          backgroundColor={
                            theme.palette.customColors.grayBackGroundColor
                          }
                          value={(() => {
                            const otherCharges = isSwageazyInventory
                              ? swageazyInventoryStore.purchaseRequestFormData
                                  .other_charges
                              : projectSourcingOpsStore.purchaseRequestBody
                                  .other_charges;
                            return otherCharges !== undefined &&
                              otherCharges !== null
                              ? otherCharges
                              : "";
                          })()}
                          onChange={(id, value) => {
                            const otherCharges =
                              value && value.trim() !== ""
                                ? Number(value)
                                : undefined;
                            if (isSwageazyInventory) {
                              const subtotal = (
                                swageazyInventoryStore.purchaseRequestProducts ||
                                []
                              ).reduce((sum, p) => {
                                return (
                                  sum + (p.quantity || 0) * (p.cost_price || 0)
                                );
                              }, 0);
                              const totalAmount =
                                subtotal + (otherCharges || 0);

                              swageazyInventoryStore.setPurchaseRequestFormData(
                                {
                                  ...swageazyInventoryStore.purchaseRequestFormData,
                                  other_charges: otherCharges,
                                  subtotal: subtotal,
                                  total_amount: totalAmount,
                                },
                              );
                            } else {
                              projectSourcingOpsStore.updateOtherCharges(
                                otherCharges,
                              );
                              projectSourcingOpsStore.calculatePurchaseRequestTotalSum();
                            }
                          }}
                        />
                      </Box>
                    </Box>
                    <Divider sx={{ mt: "0.625rem" }} />

                    <Box
                      paddingTop="0.5rem"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <CustomTypography variant="body2" fontWeight={600}>
                        Total Amount
                      </CustomTypography>
                      <CustomTypography variant="body2" fontWeight={600}>
                        ₹
                        {isSwageazyInventory
                          ? (
                              swageazyInventoryStore.purchaseRequestFormData
                                ?.total_amount || 0
                            ).toFixed(2)
                          : (
                              projectSourcingOpsStore.purchaseRequestBody
                                .total_amount || 0
                            ).toFixed(2)}
                      </CustomTypography>
                    </Box>
                    <Divider sx={{ mt: 1 }} />

                    <Box sx={{ mt: 3 }}>
                      <CustomTypography variant="body2" fontWeight={400}>
                        Note: Please fill other charges if applicable or 0 if
                        not applicable.
                      </CustomTypography>
                    </Box>
                  </Box>
                </Grid2>
              )}
            </Grid2>
          </DialogContent>

          <DialogActions
            sx={{
              padding: { xs: "16px 20px 20px", sm: "15px 24px 15px 24px" },
              borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
              backgroundColor: theme.palette.customColors.lightGreyColor,
              gap: 2,
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "auto",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    isSwageazyInventory
                      ? swageazyInventoryStore.purchaseRequestFormData
                          .is_urgent_sourcing_request || false
                      : projectSourcingOpsStore.purchaseRequestBody
                          .is_urgent_sourcing_request || false
                  }
                  onChange={handleIsUrgentSourcing}
                  sx={{
                    color: theme.palette.customColors.blueGradientStart,
                    "&.Mui-checked": {
                      color: theme.palette.customColors.blueGradientStart,
                    },
                  }}
                />
              }
              label={
                <CustomTypography variant="body2" style={{ fontWeight: 600 }}>
                  Urgent Sourcing
                </CustomTypography>
              }
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                onClick={handleClose}
                variant="outlined"
                sx={{
                  borderColor: theme.palette.customColors.greyDarkborder,
                  color: theme.palette.customColors.greyTextColor,
                  fontWeight: 600,
                  padding: "10px 24px",
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: theme.palette.customColors.greyTextColor,
                    backgroundColor: theme.palette.customColors.lightGreyColor,
                  },
                }}
              >
                Cancel
              </Button>

              <Button
                onClick={createPr}
                variant="contained"
                sx={{
                  backgroundColor:
                    theme.palette.customColors.pinkDark || "#FF6B9D",
                  color: theme.palette.customColors.white,
                  fontWeight: 600,
                  padding: "10px 24px",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.customColors.pinkDark || "#FF6B9D",
                    transform: "translateY(-1px)",
                    boxShadow: `0 4px 12px ${
                      theme.palette.customColors.pinkDark || "#FF6B9D"
                    }30`,
                  },
                }}
              >
                Create PR
              </Button>
            </Box>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={swageazyInventoryStore.popupState.openvendorPopup}
        onClose={() =>
          swageazyInventoryStore.managePopup("openvendorPopup", "close")
        }
        maxWidth="lg"
        fullWidth
        sx={{ zIndex: 2000 }}
      >
        <DialogContent>
          <AddEditVendor
            isModal={true}
            onClose={() =>
              swageazyInventoryStore.managePopup("openvendorPopup", "close")
            }
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default observer(CreatePurchaseRequestPopup);
